<template>
  <div class="progressive-image-container" :style="containerStyle">
    <div class="tiles-wrapper" :style="wrapperStyle">
      <img
        v-for="tile in loadedTiles"
        :key="`tile-${tile.x}-${tile.y}`"
        :data-tile-index="tile.index"
        :src="tile.url"
        :style="getTileStyle(tile)"
        class="tile-image"
        @load="onTileLoad"
        @error="onTileError"
      />
    </div>
    <div v-if="loading" class="loading-overlay" :style="loadingOverlayStyle">
      <div class="loading-text">加载中... {{ loadedCount }}/{{ totalTiles }}</div>
      <el-progress :percentage="loadProgress" :stroke-width="3"></el-progress>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgressiveImage',
  props: {
    // 分片ID
    tileId: {
      type: String,
      required: true
    },
    // 存储路径
    path: {
      type: String,
      required: true
    },
    // 原始图片宽度
    originalWidth: {
      type: Number,
      required: true
    },
    // 原始图片高度
    originalHeight: {
      type: Number,
      required: true
    },
    // 分片宽度
    tileWidth: {
      type: Number,
      default: 512
    },
    // 分片高度
    tileHeight: {
      type: Number,
      default: 512
    },
    // 横向分片数量
    tilesX: {
      type: Number,
      required: true
    },
    // 纵向分片数量
    tilesY: {
      type: Number,
      required: true
    },
    // 分片URL列表（如果提供，则直接使用，否则通过API获取）
    tileUrls: {
      type: Array,
      default: null
    },
    // 容器宽度（可选，如果不设置则使用原始宽度）
    containerWidth: {
      type: [Number, String],
      default: null
    },
    // 是否自动加载
    autoLoad: {
      type: Boolean,
      default: true
    },
    // 加载顺序：'row' 按行加载，'col' 按列加载，'diagonal' 对角线加载，'random' 随机加载
    loadOrder: {
      type: String,
      default: 'row',
      validator: (value) => ['row', 'col', 'diagonal', 'random'].includes(value)
    }
  },
  data () {
    return {
      tiles: [],
      loadedTiles: [],
      loading: false,
      loadedCount: 0,
      loadQueue: [],
      currentLoadIndex: 0
    }
  },
  computed: {
    totalTiles () {
      return this.tilesX * this.tilesY
    },
    loadProgress () {
      return this.totalTiles > 0 ? Math.round((this.loadedCount / this.totalTiles) * 100) : 0
    },
    containerStyle () {
      const width = this.containerWidth || this.originalWidth
      const aspectRatio = this.originalHeight / this.originalWidth

      // 如果 containerWidth 是百分比，使用 padding-top 技巧来保持宽高比
      if (this.containerWidth && typeof this.containerWidth === 'string') {
        const paddingTopPercent = aspectRatio * 100
        return {
          width: this.containerWidth,
          paddingTop: `${paddingTopPercent}%`,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        }
      }

      // 如果是数字，直接计算高度
      const height = typeof width === 'number' ? width * aspectRatio : `${width}px`

      return {
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
      }
    },
    wrapperStyle () {
      // 如果 containerWidth 是百分比，wrapper 使用绝对定位填满容器
      if (this.containerWidth && typeof this.containerWidth === 'string') {
        return {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }
      }

      // 如果是数字，计算缩放比例
      const scaleX = this.containerWidth
        ? (typeof this.containerWidth === 'number' ? this.containerWidth / this.originalWidth : 1)
        : 1
      const scaleY = this.containerWidth
        ? (typeof this.containerWidth === 'number' ? this.containerWidth / this.originalWidth : 1)
        : 1

      return {
        width: `${this.originalWidth}px`,
        height: `${this.originalHeight}px`,
        transform: `scale(${scaleX}, ${scaleY})`,
        transformOrigin: 'top left',
        position: 'relative'
      }
    },
    loadingOverlayStyle () {
      // 当使用 padding-top 技巧时，loading-overlay 需要覆盖整个容器
      if (this.containerWidth && typeof this.containerWidth === 'string') {
        return {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }
      }
      return {}
    }
  },
  watch: {
    tileUrls: {
      immediate: true,
      handler (newUrls) {
        if (newUrls && newUrls.length > 0) {
          this.initTiles(newUrls)
        }
      }
    },
    autoLoad: {
      immediate: true,
      handler (newVal) {
        if (newVal && this.tiles.length > 0) {
          this.startLoading()
        }
      }
    }
  },
  mounted () {
    if (this.tileUrls && this.tileUrls.length > 0) {
      this.initTiles(this.tileUrls)
    }
  },
  methods: {
    initTiles (urls) {
      this.tiles = []
      let index = 0
      for (let y = 0; y < this.tilesY; y++) {
        for (let x = 0; x < this.tilesX; x++) {
          const url = urls[index]
          if (url) {
            this.tiles.push({
              url,
              x,
              y,
              index,
              loaded: false,
              error: false
            })
          }
          index++
        }
      }

      // 根据加载顺序排序
      this.sortTilesByLoadOrder()

      if (this.autoLoad) {
        this.startLoading()
      }
    },
    sortTilesByLoadOrder () {
      switch (this.loadOrder) {
        case 'row':
          // 按行加载，已经是按行顺序
          break
        case 'col':
          // 按列加载
          this.tiles.sort((a, b) => {
            if (a.x !== b.x) return a.x - b.x
            return a.y - b.y
          })
          break
        case 'diagonal':
          // 对角线加载
          this.tiles.sort((a, b) => {
            const distA = a.x + a.y
            const distB = b.x + b.y
            if (distA !== distB) return distA - distB
            return a.y - b.y
          })
          break
        case 'random':
          // 随机加载
          this.tiles = this.tiles.sort(() => Math.random() - 0.5)
          break
      }
    },
    startLoading () {
      if (this.tiles.length === 0) return

      this.loading = true
      this.loadedCount = 0
      this.currentLoadIndex = 0
      this.loadedTiles = []

      // 开始加载第一个分片
      this.loadNextTile()
    },
    loadNextTile () {
      if (this.currentLoadIndex >= this.tiles.length) {
        this.loading = false
        this.$emit('load-complete', this.loadedCount)
        return
      }

      const tile = this.tiles[this.currentLoadIndex]
      if (tile && tile.url) {
        this.loadedTiles.push(tile)
        this.currentLoadIndex++
      } else {
        this.currentLoadIndex++
        this.loadNextTile()
      }
    },
    onTileLoad (event) {
      const img = event.target
      const tileIndex = parseInt(img.getAttribute('data-tile-index'), 10)
      const tile = this.loadedTiles.find(t => t.index === tileIndex)
      if (!tile || tile.loaded) return

      tile.loaded = true
      this.loadedCount++
      this.$emit('tile-loaded', { tile, loadedCount: this.loadedCount, totalTiles: this.totalTiles })

      // 继续加载下一个
      this.loadNextTile()
    },
    onTileError (event) {
      const img = event.target
      const tileIndex = parseInt(img.getAttribute('data-tile-index'), 10)
      const tile = this.loadedTiles.find(t => t.index === tileIndex)
      if (tile) {
        tile.error = true
        tile.loaded = false // 确保失败的分片不会显示
        console.error('分片加载失败:', tile)
        this.$emit('tile-error', tile)
      }

      // 即使失败也继续加载下一个
      this.loadNextTile()
    },
    getTileStyle (tile) {
      // 如果分片加载失败，隐藏它以避免影响布局
      const baseStyle = {
        position: 'absolute',
        display: tile.error ? 'none' : 'block',
        opacity: tile.loaded ? 1 : 0,
        transition: 'opacity 0.3s ease-in'
      }

      // 如果 containerWidth 是百分比，使用百分比定位和大小
      if (this.containerWidth && typeof this.containerWidth === 'string') {
        // 使用更精确的计算，保留足够的小数位避免累积误差
        const leftPercent = Number((tile.x * this.tileWidth / this.originalWidth * 100).toFixed(6))
        const topPercent = Number((tile.y * this.tileHeight / this.originalHeight * 100).toFixed(6))
        const actualTileWidth = Math.min(this.tileWidth, this.originalWidth - tile.x * this.tileWidth)
        const actualTileHeight = Math.min(this.tileHeight, this.originalHeight - tile.y * this.tileHeight)
        const widthPercent = Number((actualTileWidth / this.originalWidth * 100).toFixed(6))
        const heightPercent = Number((actualTileHeight / this.originalHeight * 100).toFixed(6))

        return {
          ...baseStyle,
          left: `${leftPercent}%`,
          top: `${topPercent}%`,
          width: `${widthPercent}%`,
          height: `${heightPercent}%`
        }
      }

      // 如果是数字或未设置，使用像素定位
      const left = tile.x * this.tileWidth
      const top = tile.y * this.tileHeight
      const width = Math.min(this.tileWidth, this.originalWidth - left)
      const height = Math.min(this.tileHeight, this.originalHeight - top)

      return {
        ...baseStyle,
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`
      }
    },
    // 手动重新加载
    reload () {
      this.startLoading()
    }
  }
}
</script>

<style scoped>
.progressive-image-container {
  position: relative;
  background-color: #f5f5f5;
}
.el-progress{
  width: 200px !important;
}
.el-progress__text {
  font-size: 8px !important;
  margin-left: 0 !important;
}
.tiles-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.tile-image {
  display: block;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.loading-text {
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
}

.loading-overlay :deep(.el-progress) {
  width: 200px;
}
</style>
